import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';

import { GuidelineFilter, User } from '@treetop/shared/model';
import { ResourceTagChipComponent } from '@treetop/shared/tt-resource-tags';

import { CheckboxComponent } from '../checkbox/checkbox.component';
import { IconDirective } from '../icon/icon.directive';
import { InputDirective, InputPrefixDirective } from '../input';
import { SpinnerComponent } from '../spinner/spinner.component';
import { SharedFilterPopoverUserSelectorComponent } from './filter-popover-user-selector/filter-popover-user-selector.component';

type ResourceTag = { id: string; label: string; color: string; backgroundColor: string };

export type GuidelineFilterPopoverFilter = {
  selectedAuthorIds?: string[];
  selectedOwnerIds?: string[];
  soonToExpire?: boolean;
  expired?: boolean;
};

@Component({
  selector: 'tt-guidelines-filter-popover',
  standalone: true,
  imports: [
    MatButtonModule,
    TranslocoModule,
    SharedFilterPopoverUserSelectorComponent,
    CheckboxComponent,
    SpinnerComponent,
    ResourceTagChipComponent,
    FormsModule,
    MatIconModule,
    IconDirective,
    InputDirective,
    InputPrefixDirective,
  ],
  templateUrl: './guideline-filter-popover.component.html',
  styleUrls: ['./guideline-filter-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidelinesFilterPopover {
  public isRevisionDateEnabled = input<boolean>(false);
  public filter = input<GuidelineFilter>();
  public loadingAuthors = input(false);
  public loadingOwners = input(false);
  public loadingResourceTags = input(false);
  public authors = input<User[]>([]);
  public owners = input<User[]>([]);
  public resourceTags = input<ResourceTag[]>([]);

  public filterChanged = output<GuidelineFilter>();

  protected resourceTagSearchTerm = signal('');
  protected filteredResourceTags = computed(() => {
    const searchTerm = this.resourceTagSearchTerm().trim().toLocaleLowerCase();
    if (!searchTerm) {
      return this.resourceTags();
    }

    return this.resourceTags().filter((tag) => tag.label.toLocaleLowerCase().includes(searchTerm));
  });

  protected handleAuthorSelected(authors: User[]) {
    this.filterChanged.emit({
      ...this.filter(),
      authors,
    });
  }

  protected handleOwnerSelected(owners: User[]) {
    this.filterChanged.emit({
      ...this.filter(),
      owners,
    });
  }

  protected handleSoonToExpireSelected(showSoonToExpire: boolean) {
    this.filterChanged.emit({
      ...this.filter(),
      showSoonToExpire,
    });
  }

  protected handleExpiredSelected(showExpired: boolean) {
    this.filterChanged.emit({
      ...this.filter(),
      showExpired,
    });
  }

  protected isResourceTagSelected(tagId: string): boolean {
    return this.filter()?.resourceTags?.some((t) => t.id === tagId) ?? false;
  }

  protected handleResourceTagSelected(tagId: string, checked: boolean) {
    const currentTags = this.filter()?.resourceTags ?? [];
    const selectedTag = this.resourceTags().find((tag) => tag.id === tagId);
    if (checked && !selectedTag) {
      return;
    }

    const resourceTags = checked && selectedTag ? [...currentTags, selectedTag] : currentTags.filter((tag) => tag.id !== tagId);
    this.filterChanged.emit({
      ...this.filter(),
      resourceTags,
    });
  }
}
